import { FormEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Search, Sparkles } from "lucide-react";

import { SPECIES, type PathologyPreset, type SpeciesKey } from "@shared/schema";
import { api } from "@/lib/api";
import { parseGeneList } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const SPECIES_ORDER: SpeciesKey[] = [
  "human",
  "mouse",
  "rat",
  "zebrafish",
  "drosophila",
  "c_elegans",
  "yeast",
  "arabidopsis",
];

interface ExampleQuery {
  label: string;
  genes: string;
  species: SpeciesKey;
  tissue?: string;
  diseaseMode?: string;
  cellType?: string;
}

const EXAMPLE_QUERIES: ExampleQuery[] = [
  { label: "TP53 tumor suppressor network", genes: "TP53, MDM2, BRCA1, CDKN1A, CHEK2", species: "human" },
  {
    label: "Muscle satellite cell: PAX7, MYF5, MYOD1",
    genes: "PAX7, MYF5, MYOD1, MYOG",
    species: "human",
    tissue: "skeletal_muscle",
    cellType: "satellite_cell",
  },
  { label: "BRCA1 DNA repair", genes: "BRCA1, BRCA2, RAD51, PALB2", species: "human" },
  {
    label: "mTOR signaling: MTOR, RPTOR, RICTOR",
    genes: "MTOR, RPTOR, RICTOR, MLST8, DEPTOR",
    species: "human",
  },
  {
    label: "Inflammasome: NLRP3, CASP1, IL1B",
    genes: "NLRP3, CASP1, IL1B, PYCARD, GSDMD",
    species: "human",
  },
  {
    label: "Ischemia-reperfusion: HIF1A, VEGFA, NOS3",
    genes: "HIF1A, VEGFA, NOS3, EPAS1, SLC2A1",
    species: "human",
    tissue: "skeletal_muscle",
    diseaseMode: "ischemia_hypoxia",
  },
];

const SOURCE_CHIPS: Array<{ label: string; href: string }> = [
  { label: "STRING v12", href: "https://string-db.org/" },
  { label: "UniProt 2025", href: "https://www.uniprot.org/" },
  { label: "Open Targets Platform", href: "https://platform.opentargets.org/" },
  { label: "AlphaFold DB 2025", href: "https://alphafold.ebi.ac.uk/" },
  { label: "Context KB v1.0", href: "#" },
];

const PLACEHOLDER =
  "Paste gene symbols, protein names, or UniProt IDs\nOne per line, or comma/space separated\n\nExamples: TP53   BRCA1 BRCA2   P04637";

export function SearchPage() {
  const [, setLocation] = useLocation();
  const [input, setInput] = useState("");
  const [species, setSpecies] = useState<SpeciesKey>("human");
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [pendingTissue, setPendingTissue] = useState<string | undefined>();
  const [pendingCellType, setPendingCellType] = useState<string | undefined>();
  const [pendingDiseaseMode, setPendingDiseaseMode] = useState<string | undefined>();
  const [pendingRepairState, setPendingRepairState] = useState<string | undefined>();

  const pathologyQuery = useQuery({
    queryKey: ["pathology-presets"],
    queryFn: () => api.listPathologyPresets(),
  });

  useEffect(() => {
    // Focus the textarea once on mount for the screenshot-perfect landing state.
    const ta = document.getElementById("search-genes") as HTMLTextAreaElement | null;
    ta?.focus();
  }, []);

  const genes = useMemo(() => parseGeneList(input), [input]);

  const applyPreset = (preset: PathologyPreset) => {
    setActivePreset(preset.id);
    setInput(preset.genes.join(", "));
    setSpecies(preset.species);
    setPendingTissue(preset.suggestedTissue);
    setPendingCellType(preset.suggestedCellType);
    setPendingDiseaseMode(preset.suggestedDiseaseMode);
    setPendingRepairState(preset.suggestedRepairState);
  };

  const applyExample = (ex: ExampleQuery) => {
    setActivePreset(null);
    setInput(ex.genes);
    setSpecies(ex.species);
    setPendingTissue(ex.tissue);
    setPendingCellType(ex.cellType);
    setPendingDiseaseMode(ex.diseaseMode);
    setPendingRepairState(undefined);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (genes.length === 0) return;
    const params = new URLSearchParams({ genes: genes.join(","), species });
    if (pendingTissue) params.set("tissue", pendingTissue);
    if (pendingCellType) params.set("cellType", pendingCellType);
    if (pendingDiseaseMode) params.set("diseaseMode", pendingDiseaseMode);
    if (pendingRepairState) params.set("repairState", pendingRepairState);
    if (activePreset) params.set("preset", activePreset);
    setLocation(`/results?${params.toString()}`);
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:py-14">
      <section className="flex flex-col items-center text-center">
        <div className="relative">
          <span
            className="grid h-11 w-11 place-items-center rounded-full text-white shadow-[0_8px_32px_-8px_hsl(186_84%_40%/0.6)]"
            style={{
              background:
                "radial-gradient(circle at 30% 30%, hsl(186 84% 50%), hsl(213 60% 22%))",
            }}
          >
            <Search className="h-5 w-5" strokeWidth={2.2} />
          </span>
        </div>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight">
          ACA<span className="text-accent">DEM</span>
        </h1>
        <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-muted-foreground">
          Context-aware protein interaction analysis. Evidence-tiered, conservation-scored, and ranked
          for experimental tractability. Optimized for skeletal muscle pathology, ischemia, fibrosis,
          and myogenesis research.
        </p>
      </section>

      <section className="mt-8 space-y-3">
        <div className="label-eyebrow flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-accent" />
          Quick Pathology Panels
        </div>
        <div className="flex flex-wrap gap-2">
          {pathologyQuery.data?.presets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset)}
              className={
                activePreset === preset.id
                  ? "chip chip--active"
                  : "chip"
              }
              title={preset.description}
            >
              {preset.label}
            </button>
          ))}
          {pathologyQuery.isPending && (
            <span className="text-xs text-muted-foreground">Loading presets…</span>
          )}
        </div>
      </section>

      <form className="mt-6 space-y-4" onSubmit={onSubmit}>
        <div className="relative">
          <textarea
            id="search-genes"
            rows={6}
            value={input}
            onChange={(e) => {
              setInput(e.currentTarget.value);
              if (activePreset) setActivePreset(null);
            }}
            placeholder={PLACEHOLDER}
            className="flex w-full rounded-lg border-2 border-accent/50 bg-card px-4 py-3 font-mono text-sm leading-relaxed text-foreground shadow-sm ring-offset-background placeholder:text-muted-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {genes.length > 0 && (
            <span className="pointer-events-none absolute right-3 top-3 rounded-full border border-border bg-background/80 px-2 py-0.5 text-[10px] font-mono tabular-nums text-muted-foreground">
              {genes.length} id{genes.length === 1 ? "" : "s"}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <select
              value={species}
              onChange={(e) => setSpecies(e.currentTarget.value as SpeciesKey)}
              className="h-11 appearance-none rounded-md border border-border bg-card px-3 pr-9 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {SPECIES_ORDER.map((k) => (
                <option key={k} value={k}>
                  {SPECIES[k].label} ({SPECIES[k].common})
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              ▾
            </span>
          </div>

          <Button
            type="submit"
            size="lg"
            disabled={genes.length === 0}
            className="h-11 flex-1 justify-center gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <Search className="h-4 w-4" />
            Search Interactions
          </Button>
        </div>
      </form>

      <section className="mt-6 space-y-2">
        <div className="label-eyebrow">Example Queries</div>
        <div className="flex flex-wrap gap-2">
          {EXAMPLE_QUERIES.map((ex) => (
            <button
              key={ex.label}
              type="button"
              onClick={() => applyExample(ex)}
              className="chip"
            >
              {ex.label}
            </button>
          ))}
        </div>
      </section>

      <section className="mt-6 flex flex-wrap items-center gap-2">
        {SOURCE_CHIPS.map((s) => (
          <a
            key={s.label}
            href={s.href}
            target={s.href === "#" ? undefined : "_blank"}
            rel={s.href === "#" ? undefined : "noreferrer"}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted"
          >
            <span className="text-accent">●</span>
            {s.label}
          </a>
        ))}
      </section>

      <div className="caveat-banner mt-6">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <p>
          <strong>All predictions are hypotheses.</strong> Evidence tiers help distinguish experimental
          support from AI-first predictions. Context scoring adjusts lab priorities based on tissue
          expression and subcellular compartment plausibility &mdash; not binding proof. Apply domain
          knowledge before committing lab resources.
        </p>
      </div>
    </div>
  );
}
